import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import assertNever from 'app/utils/assert-never';
import { Loggable } from 'app/utils/logging/loggable';

import { PresentationService } from '../../services/presentation/presentation.service';
import { ShareScreenParticipantService } from '../../services/share-screen/share-screen-participant.service';
import { getReason } from '../../services/subscription/subscription.helper';
import { SubscriptionReason, SubscriptionType } from '../../services/subscription/subscription.types';
import { VideoRoomService } from '../../services/video-room.service';
import { selectors, socialAppSelectors, SubscriptionStatus, UserSubscriptionsState } from '../../state/app';
import { SubscriptionState } from '../../types/webrtc';
import { SceneService } from '../../services/scene.service';
import { UserId } from '../../types';
import { coreSelectors } from 'app/core/state';

type SubscriptionStates = keyof typeof SubscriptionState | 'ALL';
@Component({
  selector: 'app-debugger-subscriptions',
  templateUrl: './debugger-subscriptions.component.html',
  styleUrls: ['./debugger-subscriptions.component.scss'],
  standalone: false,
})
export class DebuggerSubscriptionsComponent extends Loggable implements OnInit, OnDestroy {
  private destroyed$ = new ReplaySubject<boolean>(1);
  private subscriptions: UserSubscriptionsState = {};
  public subscriptionTypes: SubscriptionStates[] = [];
  public filteredSubscriptions: SubscriptionStatus[] = [];
  public selectedSubscriptionType: SubscriptionStates = 'ALL';
  public peerState?: RTCPeerConnectionState;
  public SubscriptionState = SubscriptionState;
  protected mainPlayerId: string | undefined;

  public constructor(
    private videoRoomService: VideoRoomService,
    private presentationService: PresentationService,
    private shareScreenParticipantService: ShareScreenParticipantService,
    private store: Store,
    private sceneService: SceneService,
  ) {
    super();
    this.setDisplayName('DebuggerSubscriptionsComponent');
    this.subscriptionTypes = Object.keys(SubscriptionState) as SubscriptionStates[];
  }

  public ngOnInit() {
    this.store
      .select(selectors.getSubscriptionStatus)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((subscriptions) => {
        this.subscriptions = subscriptions ?? {};
        this.filterSubscriptions(subscriptions ?? {});
      });

    this.store
      .select(socialAppSelectors.getPublishState)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((peerState) => (this.peerState = peerState));

    this.store
      .select(coreSelectors.getUserId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((mainPlayerId) => (this.mainPlayerId = mainPlayerId));
  }

  public onSelectionChange(type: keyof typeof SubscriptionState | 'ALL') {
    this.selectedSubscriptionType = type;
    this.filterSubscriptions(this.subscriptions);
  }

  public filterSubscriptions(subscriptions: UserSubscriptionsState): void {
    const subs = Object.values(subscriptions);
    if (this.selectedSubscriptionType === 'ALL') {
      this.filteredSubscriptions = subs;
      return;
    }
    this.filteredSubscriptions = subs.filter((s) => s.state === this.selectedSubscriptionType);
  }

  public getReason(reason?: SubscriptionReason): string {
    return getReason(reason);
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public async onRepublish() {
    this.warn('DEBUGGER: Republish');
    await this.videoRoomService.republish();
  }

  public async onPeerFail() {
    this.warn('DEBUGGER: Peer failure');
    await this.videoRoomService.simulatePeerMediaFailure();
  }

  public onClosePublishPeer(): void {
    this.warn('DEBUGGER: Close publisher');
    this.videoRoomService.closePublishPeer();
  }

  protected onKillPixi(subscription: SubscriptionStatus | UserId): void {
    if (typeof subscription === 'string') {
      this.sceneService.getPlayer(subscription)?.destroyUser();
      return;
    }
    this.sceneService.getPlayer(subscription.id)?.destroyUser();
  }

  public onCloseSubscriberPeer(subscription: SubscriptionStatus): void {
    this.warn('DEBUGGER: Close subscriber', { subscription });
    switch (subscription.type) {
      case SubscriptionType.PARTICIPANT:
        this.videoRoomService.closeSubscriberPeer(subscription.id);
        break;
      case SubscriptionType.MIX:
      case SubscriptionType.MIRROR:
        this.presentationService.closeSubscriberPeer(subscription.type, subscription.id);
        break;
      case SubscriptionType.SCREEN_SHARE:
        this.shareScreenParticipantService.closeSubscriberPeer();
        break;
      case SubscriptionType.LIVE_STREAM:
        this.warn('DEBUGGER: Close subscriber not implemented for', { subscription });
        return;
      default:
        assertNever(subscription.type);
    }
  }
}
